import { DELIVERY_STATUS_LABELS, type DeliveryStatus, type Direction, type MessageAuthor } from '@lia/contracts';
import { AlertCircleIcon, Clock01Icon, HelpCircleIcon, InformationCircleIcon, RoboticIcon, SmartPhone01Icon, SquareLock02Icon, Tick02Icon, TickDouble02Icon, UserIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatTime } from '../../lib/format.js';

const DELIVERY_ICON: Record<DeliveryStatus, React.ReactNode> = {
  queued: <Icon icon={Clock01Icon} aria-hidden />,
  provider_accepted: <Icon icon={Tick02Icon} aria-hidden />,
  delivered: <Icon icon={TickDouble02Icon} aria-hidden />,
  read: <Icon icon={TickDouble02Icon} aria-hidden />,
  failed: <Icon icon={AlertCircleIcon} aria-hidden />,
  unknown: <Icon icon={HelpCircleIcon} aria-hidden />,
};

const DELIVERY_CLASS: Record<DeliveryStatus, string> = {
  queued: 'text-text-secondary',
  provider_accepted: 'text-text-secondary',
  delivered: 'text-text-secondary',
  read: 'text-success font-medium',
  failed: 'text-danger font-medium',
  unknown: 'text-warning',
};

export interface DeliveryStatusLabelProps {
  status: DeliveryStatus;
  className?: string;
}

/** Icon + Spanish text for a message delivery status (never icon-only). */
export function DeliveryStatusLabel({ status, className }: DeliveryStatusLabelProps) {
  return (
    <span className={cn('inline-flex items-center gap-1 text-meta [&_svg]:size-3.5', DELIVERY_CLASS[status], className)}>
      {DELIVERY_ICON[status]}
      {DELIVERY_STATUS_LABELS[status]}
    </span>
  );
}

export interface MessageBubbleProps {
  direction: Direction;
  author: MessageAuthor;
  /** Display name: contact name, advisor name. AI defaults to "Asistente IA". */
  authorName?: string;
  body: React.ReactNode;
  sentAt: string;
  timezone: string;
  /** Outbound only. */
  deliveryStatus?: DeliveryStatus;
  /** Provider failure reason for failed deliveries (plain text, no secrets). */
  failureReason?: string;
  /** Internal team note: never sent to the contact. */
  internalNote?: boolean;
  /** Sent by a person from the WhatsApp Business app (coexistence), not from this application. */
  sentFromBusinessApp?: boolean;
  /** Media previews or extra content below the text. */
  attachments?: React.ReactNode;
  className?: string;
}

/** Conversation message. Author styles differ and are always labelled in text. */
export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(function MessageBubble(
  { direction, author, authorName, body, sentAt, timezone, deliveryStatus, failureReason, internalNote, sentFromBusinessApp, attachments, className },
  ref,
) {
  const time = formatTime(sentAt, timezone);

  if (author === 'system' && !internalNote) {
    return (
      <div ref={ref} role="note" className={cn('flex justify-center py-1', className)}>
        <p className="inline-flex max-w-[85%] items-center gap-1.5 rounded-pill bg-surface-subtle px-3 py-1 text-center text-meta text-text-secondary">
          <Icon icon={InformationCircleIcon} aria-hidden className="size-3.5 shrink-0" />
          <span>
            <span className="sr-only">Sistema: </span>
            {body} · <span className="ef-tabular">{time}</span>
          </span>
        </p>
      </div>
    );
  }

  const outbound = direction === 'outbound' || internalNote;
  const label = internalNote
    ? `Nota interna · ${authorName ?? 'Equipo'}`
    : author === 'ai'
      ? (authorName ?? 'Asistente IA')
      : author === 'human'
        ? (authorName ?? 'Asesor')
        : (authorName ?? 'Contacto');
  const icon = internalNote ? <Icon icon={SquareLock02Icon} aria-hidden /> : author === 'ai' ? <Icon icon={RoboticIcon} aria-hidden /> : author === 'human' ? <Icon icon={UserIcon} aria-hidden /> : null;

  const bubbleClass = internalNote
    ? 'border border-dashed border-warning bg-warning-bg text-text-primary'
    : author === 'ai'
      ? 'border border-border-subtle bg-surface-subtle text-text-primary'
      : author === 'human'
        ? 'bg-accent-selected text-accent-on-selected'
        : 'border border-border-subtle bg-surface-default text-text-primary';

  return (
    <div ref={ref} className={cn('flex', outbound ? 'justify-end' : 'justify-start', className)}>
      <div className={cn('grid max-w-[min(85%,560px)] gap-1', outbound ? 'justify-items-end' : 'justify-items-start')}>
        <span className={cn('inline-flex items-center gap-1 text-meta font-medium [&_svg]:size-3.5', internalNote ? 'text-warning' : 'text-text-secondary')}>
          {icon}
          {label}
        </span>
        <div className={cn('rounded-card px-3.5 py-2.5 text-body whitespace-pre-wrap break-words', outbound ? 'rounded-tr-[4px]' : 'rounded-tl-[4px]', bubbleClass)}>
          {body}
          {attachments ? <div className="mt-2 grid gap-2">{attachments}</div> : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-meta text-text-secondary">
          <time dateTime={sentAt} className="ef-tabular">
            {time}
          </time>
          {internalNote ? <span>Solo visible para el equipo</span> : null}
          {sentFromBusinessApp ? (
            <span className="inline-flex items-center gap-1">
              <Icon icon={SmartPhone01Icon} aria-hidden className="size-3.5" />
              Enviado desde la app de WhatsApp Business
            </span>
          ) : null}
          {!internalNote && direction === 'outbound' && deliveryStatus ? <DeliveryStatusLabel status={deliveryStatus} /> : null}
        </div>
        {deliveryStatus === 'failed' && failureReason ? <p className="text-meta text-danger">{failureReason}</p> : null}
      </div>
    </div>
  );
});
